// src/app/services/auth.service.ts
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  InteractionStatus,
  RedirectRequest,
  EventMessage,
  EventType,
  AuthenticationResult,
  AccountInfo,
  PopupRequest,
} from '@azure/msal-browser';
import { Subject, filter, takeUntil, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { OrganizationService } from './organization.service';
import { LoadingService } from './loading.service';
import {
  ORG_CHECK_MAX_RETRIES,
  ORG_CHECK_RETRY_DELAYS_MS,
} from '../constants/timing.constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _destroying$ = new Subject<void>();
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly router = inject(Router);
  private readonly organizationService = inject(OrganizationService);
  private readonly loadingService = inject(LoadingService);

  private _user: AccountInfo | null = null;
  isLoading = false;

  constructor() {
    // Listen for successful login events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(async (result: EventMessage) => {
        try {
          const payload = result.payload as AuthenticationResult;
          this.msalService.instance.setActiveAccount(payload.account);
          this.setUser();

          // Store authority from successful login for future logout
          if (payload.authority) {
            localStorage.setItem('msal.login.authority', payload.authority);
          }

          // Show loading while checking organization
          this.loadingService.show('Setting up your account...');

          // Check organization status after successful login
          await this.checkOrganizationStatusAndRedirect();

          this.isLoading = false;
        } catch (error) {
          console.error('Post-login flow failed:', error);
          this.isLoading = false;
          this.loadingService.hide();
          // Navigate to error page as fallback
          this.router.navigate(['/error'], {
            queryParams: { type: 'api-failure' },
          });
        }
      });

    // Listen for login failure events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        console.error('Login failed:', result.error);
        this.isLoading = false;
        // Navigate to login on failure
        this.router.navigate(['/login']);
      });

    // Listen for logout events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        // Clear user state
        this._user = null;
        this.isLoading = false;
        // Clear stored authority
        localStorage.removeItem('msal.login.authority');
      });

    // Set active account when interaction completes
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.setUser();
        this.checkAndSetActiveAccount();
      });
  }

  /**
   * Sign in with redirect (default method - uses configured authority)
   */
  signIn(): void {
    if (this.isLoading) {
      return;
    }

    // Check if already authenticated
    if (this.isAuthenticated()) {
      console.log('Already authenticated, navigating to home');
      this.router.navigate(['/']);
      return;
    }

    this.isLoading = true;
    const request: RedirectRequest = {
      scopes: environment.apiConfig.scopes,
      prompt: 'select_account',
    };

    console.log('Starting login redirect');
    this.msalService.loginRedirect(request).subscribe({
      error: (error: any) => {
        console.error('Login redirect failed:', error);
        this.isLoading = false;
      },
    });
  }

  /**
   * Sign in with MYI (CIAM) - Personal accounts through MYI tenant
   */
  signInWithMYI(): void {
    if (this.isLoading) {
      return;
    }

    // Check if already authenticated
    if (this.isAuthenticated()) {
      console.log('Already authenticated, navigating to home');
      this.router.navigate(['/']);
      return;
    }

    this.isLoading = true;
    const authority = environment.msalConfig.authorities.myi;
    const request: RedirectRequest = {
      scopes: environment.apiConfig.scopes,
      authority: authority,
      prompt: 'select_account',
    };

    // Store the authority being used for login
    localStorage.setItem('msal.login.authority', authority);

    this.msalService.loginRedirect(request).subscribe({
      error: (error: any) => {
        console.error('MYI login redirect failed:', error);
        this.isLoading = false;
      },
    });
  }

  /**
   * Sign in with Work Account - Multi-tenant for any organization
   */
  signInWithWorkAccount(): void {
    if (this.isLoading) {
      return;
    }

    // Check if already authenticated
    if (this.isAuthenticated()) {
      console.log('Already authenticated, navigating to home');
      this.router.navigate(['/']);
      return;
    }

    this.isLoading = true;
    const authority = environment.msalConfig.authorities.organizations;
    const request: RedirectRequest = {
      scopes: environment.apiConfig.scopes,
      authority: authority,
      prompt: 'select_account',
    };

    // Store the authority being used for login
    localStorage.setItem('msal.login.authority', authority);

    this.msalService.loginRedirect(request).subscribe({
      error: (error: any) => {
        console.error('Work account login redirect failed:', error);
        this.isLoading = false;
      },
    });
  }

  /**
   * Sign in with popup (alternative method)
   */
  signInWithPopup(): void {
    this.isLoading = true;
    const request: PopupRequest = {
      scopes: environment.apiConfig.scopes,
      prompt: 'select_account',
    };

    this.msalService.loginPopup(request).subscribe({
      next: async (result: AuthenticationResult) => {
        this.msalService.instance.setActiveAccount(result.account);
        this.setUser();

        // Check organization status after successful popup login
        await this.checkOrganizationStatusAndRedirect();

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Login failed:', error);
        this.isLoading = false;
      },
    });
  }

  /**
   * Sign out with robust authority matching
   */
  signOut(): void {
    // Clear the loginHint to prevent auto-login after logout
    localStorage.removeItem('loginHint');

    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      // No account, use default authority
      this.msalService.logoutRedirect({
        postLogoutRedirectUri:
          environment.msalConfig.auth.postLogoutRedirectUri,
      });
      return;
    }

    // Method 1: Retrieve stored authority from login
    let logoutAuthority = localStorage.getItem('msal.login.authority');

    // Method 2: Fallback to account's environment property
    if (!logoutAuthority && account.environment) {
      if (account.environment.includes('ciamlogin.com')) {
        logoutAuthority = environment.msalConfig.authorities.myi;
      } else if (account.environment.includes('login.microsoftonline.com')) {
        logoutAuthority = environment.msalConfig.authorities.organizations;
      }
    }

    // Method 3: Final fallback to default authority
    if (!logoutAuthority) {
      logoutAuthority = environment.msalConfig.auth.authority;
    }

    const logoutRequest = {
      account: account,
      authority: logoutAuthority,
      postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri,
    };

    this.msalService.logoutRedirect(logoutRequest);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    try {
      return this.msalService.instance.getAllAccounts().length > 0;
    } catch (error) {
      // MSAL not yet initialized
      console.warn('MSAL not yet initialized:', error);
      return false;
    }
  }

  /**
   * Get current user
   */
  getUser(): AccountInfo | null {
    return this._user;
  }

  /**
   * Get user display name
   */
  getUserDisplayName(): string {
    if (!this._user) return '';

    // External ID stores user info in idTokenClaims
    const claims: any = this._user.idTokenClaims;

    // Try different name fields in order of preference
    if (claims?.name) {
      return claims.name;
    }

    // Only combine given_name + family_name if both are available
    if (claims?.given_name && claims?.family_name) {
      return `${claims.given_name} ${claims.family_name}`;
    }

    // Try individual name parts
    if (claims?.given_name) {
      return claims.given_name;
    }

    if (claims?.family_name) {
      return claims.family_name;
    }

    // Fallback to account name or email
    return this._user.name || this.getUserEmail() || 'User';
  }

  /**
   * Get user email
   */
  getUserEmail(): string {
    if (!this._user) return '';

    const claims: any = this._user.idTokenClaims;
    // External ID typically stores email in 'email' or 'preferred_username'
    return (
      claims?.email || claims?.preferred_username || this._user.username || ''
    );
  }

  /**
   * Get user initials
   */
  getUserInitials(): string {
    const name = this.getUserDisplayName();
    if (!name) return 'U';

    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  /**
   * Set current user from MSAL
   */
  private setUser(): void {
    try {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this._user = accounts[0];
        const activeAccount = this.msalService.instance.getActiveAccount();
        if (!activeAccount) {
          this.msalService.instance.setActiveAccount(accounts[0]);
        }
      } else {
        this._user = null;
      }
    } catch (error) {
      console.warn('Error setting user, MSAL not initialized:', error);
      this._user = null;
    }
  }

  /**
   * Check and set active account
   */
  private checkAndSetActiveAccount(): void {
    try {
      const activeAccount = this.msalService.instance.getActiveAccount();

      if (
        !activeAccount &&
        this.msalService.instance.getAllAccounts().length > 0
      ) {
        const accounts = this.msalService.instance.getAllAccounts();
        this.msalService.instance.setActiveAccount(accounts[0]);
      }
    } catch (error) {
      console.warn(
        'Error checking active account, MSAL not initialized:',
        error,
      );
    }
  }

  /**
   * Check organization status after login and redirect appropriately
   * Includes retry logic with exponential backoff
   */
  private async checkOrganizationStatusAndRedirect(): Promise<void> {
    const user = this.getUser();
    if (!user?.localAccountId) {
      console.warn('No user found after login');
      this.loadingService.hide();
      this.router.navigate(['/login']);
      return;
    }

    // Retry configuration - exponential backoff
    const maxRetries = ORG_CHECK_MAX_RETRIES;
    const retryDelays = ORG_CHECK_RETRY_DELAYS_MS;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Check if user has an existing organization
        const response = await firstValueFrom(
          this.organizationService.checkUserOrganization(user.localAccountId),
        );

        if (response?.data?.organizationId) {
          // Check organization status
          const orgStatus = response.data.status;

          if (orgStatus && orgStatus !== 'Active') {
            // Organization is Inactive or Suspended - block access
            console.log('Organization is not active:', orgStatus);
            this.loadingService.hide();
            this.router.navigate(['/account-inactive']);
            return;
          }

          // User has an active organization - redirect to home
          console.log('User has organization:', response.data.organizationId);
          this.loadingService.hide();
          this.router.navigate(['/']);
          return;
        } else {
          // User doesn't have an organization - redirect to signup process
          console.log('User needs to create organization');
          this.loadingService.hide();
          this.router.navigate(['/onboarding/signup-process']);
          return;
        }
      } catch (error: any) {
        console.error(
          `Error checking organization status (attempt ${attempt + 1}/${maxRetries}):`,
          error,
        );

        // Check error type
        if (error.status === 404) {
          // 404 means user definitely has no organization - no need to retry
          console.log('User has no organization (404)');
          this.loadingService.hide();
          this.router.navigate(['/onboarding/signup-process']);
          return;
        }

        // If this was the last attempt, redirect to error page
        if (attempt === maxRetries - 1) {
          console.error('All retry attempts failed, redirecting to error page');
          this.loadingService.hide();
          this.router.navigate(['/error'], {
            queryParams: {
              type:
                error.status === 0
                  ? 'network-error'
                  : 'organization-check-failed',
            },
          });
          return;
        }

        // Wait before retrying (exponential backoff)
        console.log(
          `Retrying in ${retryDelays[attempt]}ms... (${attempt + 1}/${maxRetries})`,
        );
        await new Promise((resolve) =>
          setTimeout(resolve, retryDelays[attempt]),
        );
      }
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
